import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { useAuth } from '@/lib/auth/contexto';
import { request } from '@/lib/http';
import {
  type AdicionarMembroRequest,
  type AtualizarEmpresaRequest,
  type Contador,
  type CriarEmpresaRequest,
  empresaDetalheSchema,
  empresaResumoSchema,
  type ListarEmpresasQuery,
  membroSchema,
  type ParametrosFiscais,
  type ParametrosClassificacao,
  parametrosClassificacaoSchema,
} from '@fiscoai/contracts';

export type RecursoLista = 'regimes' | 'inscricoes-estaduais' | 'cnaes' | 'codigos-externos';

export function useEmpresas(filtros: Partial<ListarEmpresasQuery> = {}) {
  return useQuery({
    queryKey: ['empresas', filtros],
    queryFn: ({ signal }) =>
      request('/empresas', { schema: z.array(empresaResumoSchema), query: filtros, signal }),
  });
}

export function useEmpresa(id: string) {
  return useQuery({
    queryKey: ['empresas', 'detalhe', id],
    queryFn: ({ signal }) => request(`/empresas/${id}`, { schema: empresaDetalheSchema, signal }),
  });
}

/** Toda escrita devolve o detalhe atualizado: grava direto no cache (sem refetch). */
function useEscritaEmpresa<TVars>(
  id: string,
  executar: (vars: TVars) => Promise<z.infer<typeof empresaDetalheSchema>>,
) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: executar,
    onSuccess: (detalhe) => {
      cliente.setQueryData(['empresas', 'detalhe', id], detalhe);
      void cliente.invalidateQueries({ queryKey: ['empresas'], exact: false, refetchType: 'none' });
    },
  });
}

export function useAtualizarEmpresa(id: string) {
  return useEscritaEmpresa(id, (dados: AtualizarEmpresaRequest) =>
    request(`/empresas/${id}`, { method: 'PATCH', body: dados, schema: empresaDetalheSchema }),
  );
}

export function useSubstituirLista(id: string, recurso: RecursoLista) {
  return useEscritaEmpresa(id, (itens: unknown[]) =>
    request(`/empresas/${id}/${recurso}`, {
      method: 'PUT',
      body: { itens },
      schema: empresaDetalheSchema,
    }),
  );
}

export function useDefinirParametros(id: string) {
  return useEscritaEmpresa(id, (dados: ParametrosFiscais) =>
    request(`/empresas/${id}/parametros-fiscais`, {
      method: 'PUT',
      body: dados,
      schema: empresaDetalheSchema,
    }),
  );
}

export function useDefinirContador(id: string) {
  return useEscritaEmpresa(id, (dados: Contador | null) =>
    request(`/empresas/${id}/contador`, {
      method: 'PUT',
      body: dados,
      schema: empresaDetalheSchema,
    }),
  );
}

export function useCriarEmpresa() {
  const cliente = useQueryClient();
  const { recarregarUsuario } = useAuth();
  return useMutation({
    mutationFn: (dados: CriarEmpresaRequest) =>
      request('/empresas', { method: 'POST', body: dados, schema: empresaDetalheSchema }),
    // Empresa nova entra no escopo de quem acessa todas: menu e permissões vêm do usuário.
    onSuccess: () =>
      Promise.all([cliente.invalidateQueries({ queryKey: ['empresas'] }), recarregarUsuario()]),
  });
}

export function useMembros(id: string, habilitado: boolean) {
  return useQuery({
    queryKey: ['empresas', id, 'membros'],
    queryFn: ({ signal }) =>
      request(`/empresas/${id}/membros`, { schema: z.array(membroSchema), signal }),
    enabled: habilitado,
  });
}

export function useAlterarMembros(id: string) {
  const cliente = useQueryClient();
  const recarregar = () => cliente.invalidateQueries({ queryKey: ['empresas', id, 'membros'] });
  const adicionar = useMutation({
    mutationFn: (dados: AdicionarMembroRequest) =>
      request(`/empresas/${id}/membros`, { method: 'POST', body: dados, schema: membroSchema }),
    onSuccess: recarregar,
  });
  const remover = useMutation({
    mutationFn: (usuarioId: string) =>
      request(`/empresas/${id}/membros/${usuarioId}`, { method: 'DELETE', schema: z.unknown() }),
    onSuccess: recarregar,
  });
  return { adicionar, remover };
}

/** Parâmetros da etapa 2 (classificação); sem configuração gravada vale o padrão do contrato. */
export function useParametrosClassificacao(id: string) {
  return useQuery({
    queryKey: ['empresas', id, 'parametros-classificacao'],
    queryFn: ({ signal }) =>
      request(`/empresas/${id}/parametros-classificacao`, {
        schema: parametrosClassificacaoSchema,
        signal,
      }),
  });
}

export function useDefinirParametrosClassificacao(id: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (dados: ParametrosClassificacao) =>
      request(`/empresas/${id}/parametros-classificacao`, {
        method: 'PUT',
        body: dados,
        schema: parametrosClassificacaoSchema,
      }),
    onSuccess: (dados) => {
      cliente.setQueryData(['empresas', id, 'parametros-classificacao'], dados);
    },
  });
}
